import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Clock, CheckCircle, ChevronRight, Truck, Search, Filter, ClipboardCheck, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const QualityCheckList = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch MOCs where DC is confirmed, QC is passed, or already dispatched for audit
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "in", ["invoice_ready", "dc_confirmed", "qc_passed", "dispatched"]),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const mocList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching QC Queue: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => 
      moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moc.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      moc.challanNo?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mocs, searchTerm]);

  const formatDate = (dateString) => {
      if (!dateString) return '---';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'invoice_ready':
        case 'dc_confirmed':
            return <div style={{fontSize: '11px', color: '#6366f1', fontWeight:'bold'}}>QC PENDING 🔍</div>;
        case 'qc_passed':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>QC PASSED ✅</div>;
        case 'dispatched':
            return <div style={{fontSize: '11px', color: '#10b981', fontWeight:'bold'}}>DISPATCHED ✅</div>;
        default:
            return <div style={{fontSize: '11px', color: '#64748b', fontWeight:'bold'}}>{status?.replace(/_/g, ' ')?.toUpperCase()}</div>;
    }
  };

  const handleRowClick = (moc) => {
    navigate(`/ops/qc/${moc.id}`);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Quality Check (MOC Gate)</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Verify material counts, record vehicle details, and confirm final exit.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Customer, MOC No, DC No or Order No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
      </div>

      {mocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <UserCheck size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No material waiting for Quality Check!</h3>
          <p style={{ color: '#64748b' }}>Items appear here once the Delivery Challan is confirmed.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>DC Ready Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Customer</th>
                <th style={thStyle}>Order No.</th>
                <th style={thStyle}>DC Number</th>
                <th style={thStyle}>MOC Number</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => (
                <tr 
                  key={moc.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => handleRowClick(moc)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>{formatDate(moc.updatedAt?.toDate ? moc.updatedAt.toDate() : moc.updatedAt)}</td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{moc.customerName}</div>
                  </td>
                  <td style={tdStyle}>{moc.orderNumber || '---'}</td>
                  <td style={tdStyle}>{moc.challanNo || '---'}</td>
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold', fontSize: '13px'}}>{moc.mocNo}</div>
                  </td>
                  <td style={tdStyle}>{moc.warehouse}</td>
                  <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      {['invoice_ready', 'dc_confirmed'].includes(moc.dispatchStatus) ? 'Perform QC' : (moc.dispatchStatus === 'qc_passed' ? 'Confirm Dispatch' : 'View Details')} <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

const filterBarStyle = { display: 'flex', gap: '15px', marginBottom: '20px' };
const searchInputStyle = { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

export default QualityCheckList;
